import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Mail, ExternalLink, Trash2, Clock, CheckCircle, XCircle, Sparkles, Zap, BarChart3, Bug, RefreshCw } from 'lucide-react';
import { SmartApplyJob, useSmartApply } from '@/hooks/useSmartApply';
import { SmartApplyModal } from '@/components/SmartApplyModal';
import { ChromeExtensionPrompt } from '@/components/ChromeExtensionPrompt';
import { toast } from 'sonner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export default function SmartApplyQueue() {
  const [jobs, setJobs] = useState<SmartApplyJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<SmartApplyJob | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { deleteFromQueue } = useSmartApply();
  const [extensionTasks, setExtensionTasks] = useState<any[]>([]);
  const [debugOpen, setDebugOpen] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);

  useEffect(() => {
    loadQueue();
    loadExtensionTasks();
    
    // Subscribe to realtime updates for smart_apply_queue
    const queueChannel = supabase
      .channel('smart_apply_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'smart_apply_queue',
        },
        () => {
          loadQueue();
        }
      )
      .subscribe();

    // Subscribe to realtime updates for extension_tasks
    const tasksChannel = supabase
      .channel('extension_tasks_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'extension_tasks',
        },
        (payload) => {
          console.log('Extension task updated:', payload.new);
          loadExtensionTasks();
          
          // Show toast for status changes
          if (payload.eventType === 'UPDATE' && payload.new) {
            const task = payload.new as any;
            if (task.status === 'applied') {
              toast.success(`Application submitted successfully! ✓`);
            } else if (task.status === 'failed') {
              toast.error(`Application failed: ${task.error_detail || 'Unknown error'}`);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(queueChannel);
      supabase.removeChannel(tasksChannel);
    };
  }, []);

  const loadQueue = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('smart_apply_queue')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs((data || []) as SmartApplyJob[]);
    } catch (error) {
      console.error('Error loading queue:', error);
      toast.error('Failed to load Smart Apply queue');
    } finally {
      setLoading(false);
    }
  };

  const loadExtensionTasks = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('extension_tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setExtensionTasks(data || []);
    } catch (error) {
      console.error('Error loading extension tasks:', error);
    }
  };

  const createTestTask = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user profile for contact info
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      const testTask = {
        user_id: user.id,
        job_url: 'https://jobs.lever.co/example/test-job-posting',
        provider: 'lever',
        payload: {
          first_name: profile?.full_name?.split(' ')[0] || 'Test',
          last_name: profile?.full_name?.split(' ')[1] || 'User',
          full_name: profile?.full_name || 'Test User',
          email: profile?.email || 'test@example.com',
          phone: profile?.phone || '555-000-0000',
          resume_url: null,
          cover_letter_text: 'This is a test application from LoopBaan Smart Apply system.',
          linkedin_url: profile?.linkedin_url || '',
          website_url: profile?.portfolio_url || ''
        },
        status: 'queued' as const
      };

      const { data, error } = await supabase
        .from('extension_tasks')
        .insert([testTask])
        .select()
        .single();

      if (error) throw error;

      toast.success(`Test task created: ${data.id.substring(0, 8)}...`);
      loadExtensionTasks();
    } catch (error) {
      console.error('Error creating test task:', error);
      toast.error('Failed to create test task');
    }
  };

  const handleReviewApply = (job: SmartApplyJob) => {
    if (job.status !== 'ready') {
      toast.info('Materials are still being prepared. Please wait...');
      return;
    }
    setSelectedJob(job);
    setShowModal(true);
  };

  const handleDelete = async (jobId: string) => {
    const success = await deleteFromQueue(jobId);
    if (success) {
      setJobs(jobs.filter(j => j.id !== jobId));
    }
  };

  const getStatusBadge = (status: string, hasExtensionTask: boolean) => {
    switch (status) {
      case 'preparing':
        return (
          <Badge variant="secondary" className="gap-1">
            <Clock className="h-3 w-3" />
            AI Generating...
          </Badge>
        );
      case 'ready':
        if (hasExtensionTask) {
          return (
            <Badge className="gap-1 bg-amber-500/20 text-amber-500 hover:bg-amber-500/30">
              <Zap className="h-3 w-3" />
              Queued for Auto-Apply
            </Badge>
          );
        }
        return (
          <Badge className="gap-1 bg-green-500/20 text-green-500 hover:bg-green-500/30">
            <CheckCircle className="h-3 w-3" />
            Ready
          </Badge>
        );
      case 'applied':
        return (
          <Badge className="gap-1 bg-blue-500/20 text-blue-500 hover:bg-blue-500/30">
            <CheckCircle className="h-3 w-3" />
            Applied ✓
          </Badge>
        );
      case 'skipped':
        return (
          <Badge variant="outline" className="gap-1">
            <XCircle className="h-3 w-3" />
            Skipped
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div>
            <Skeleton className="h-9 w-64 mb-2" />
            <Skeleton className="h-5 w-96" />
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-24" />
              </Card>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Stats for dashboard
  const stats = {
    preparing: jobs.filter(j => j.status === 'preparing').length,
    ready: jobs.filter(j => j.status === 'ready').length,
    applied: jobs.filter(j => j.status === 'applied').length,
    total: jobs.length,
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            Smart Apply Queue
          </h1>
          <p className="text-muted-foreground mt-2">
            AI generates custom resume & cover letter → Automatically queues for Chrome Extension auto-apply
          </p>
        </div>

        {/* Stats Overview */}
        {jobs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Jobs</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-muted-foreground" />
              </div>
            </Card>
            <Card className="p-4 border-orange-500/20 bg-orange-500/5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 dark:text-orange-400">Preparing</p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.preparing}</p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </Card>
            <Card className="p-4 border-green-500/20 bg-green-500/5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Ready to Apply</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.ready}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </Card>
            <Card className="p-4 border-blue-500/20 bg-blue-500/5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 dark:text-blue-400">Applied</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.applied}</p>
                </div>
                <Zap className="h-8 w-8 text-blue-500" />
              </div>
            </Card>
          </div>
        )}

        {/* Chrome Extension Prompt */}
        <ChromeExtensionPrompt variant="full" showStatus={true} />

        {/* Debug Panel: Extension Tasks */}
        <Card className="border-purple-500/20 bg-purple-500/5">
          <Collapsible open={debugOpen} onOpenChange={setDebugOpen}>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bug className="h-5 w-5 text-purple-500" />
                  <h3 className="font-semibold text-purple-600 dark:text-purple-400">
                    🔍 Debug: Extension Tasks ({extensionTasks.length})
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTasksLoading(true);
                      loadExtensionTasks().finally(() => setTasksLoading(false));
                    }}
                    disabled={tasksLoading}
                    className="gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${tasksLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm">
                      {debugOpen ? 'Hide' : 'Show'}
                    </Button>
                  </CollapsibleTrigger>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mt-2">
                Real-time view of tasks being processed by the Chrome Extension
              </p>
            </div>

            <CollapsibleContent>
              <div className="px-4 pb-4 space-y-4">
                {/* Test Buttons */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={createTestTask}
                    className="gap-2"
                  >
                    <Zap className="h-4 w-4" />
                    Create Test Task
                  </Button>
                  
                  {stats.ready > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const readyJobs = jobs.filter(j => j.status === 'ready');
                          let created = 0;
                          
                          for (const job of readyJobs) {
                            const { data, error } = await supabase.functions.invoke('smart-apply-to-extension', {
                              body: { queueId: job.id, action: 'create_task' }
                            });
                            
                            if (!error) created++;
                          }
                          
                          toast.success(`Created ${created} extension tasks!`);
                          loadQueue();
                          loadExtensionTasks();
                        } catch (error) {
                          console.error('Error creating tasks:', error);
                          toast.error('Failed to create tasks');
                        }
                      }}
                      className="gap-2"
                    >
                      <Zap className="h-4 w-4" />
                      Create {stats.ready} Real Tasks
                    </Button>
                  )}
                </div>

                {/* Tasks Table */}
                {extensionTasks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No extension tasks yet. Click "Create Test Task" to test the flow.
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[100px]">Status</TableHead>
                          <TableHead>Job URL</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Result</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {extensionTasks.map((task) => {
                          const payload = task.payload || {};
                          const email = payload.email || payload.contact?.email || payload.answers?.email || 'N/A';
                          
                          return (
                            <TableRow key={task.id}>
                              <TableCell>
                                {task.status === 'queued' && (
                                  <Badge variant="secondary">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Queued
                                  </Badge>
                                )}
                                {task.status === 'in_progress' && (
                                  <Badge className="bg-blue-500/20 text-blue-500">
                                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                    Processing
                                  </Badge>
                                )}
                                {task.status === 'applied' && (
                                  <Badge className="bg-green-500/20 text-green-500">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Applied
                                  </Badge>
                                )}
                                {task.status === 'failed' && (
                                  <Badge variant="destructive">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Failed
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="max-w-[300px] truncate">
                                {task.job_url ? (
                                  <a 
                                    href={task.job_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:underline flex items-center gap-1"
                                  >
                                    {task.job_url.substring(0, 50)}...
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                ) : (
                                  <span className="text-red-500">❌ No URL</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{task.provider || 'generic'}</Badge>
                              </TableCell>
                              <TableCell className="text-sm">
                                {email}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {new Date(task.created_at).toLocaleString()}
                              </TableCell>
                              <TableCell className="max-w-[200px] truncate text-xs">
                                {task.error_code && (
                                  <span className="text-red-500">
                                    {task.error_code}: {task.error_detail}
                                  </span>
                                )}
                                {task.result && (
                                  <span className="text-muted-foreground">
                                    {JSON.stringify(task.result).substring(0, 50)}...
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {jobs.length === 0 ? (
          <Card className="p-12 text-center">
            <Sparkles className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No jobs in your Smart Apply queue</h3>
            <p className="text-muted-foreground mb-6">
              Save jobs from the job search page to get started with Smart Apply
            </p>
            <Button asChild>
              <a href="/app/job-search">Browse Jobs</a>
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Card key={job.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {job.logo && (
                        <img 
                          src={job.logo} 
                          alt={job.company_name} 
                          className="w-12 h-12 rounded object-contain"
                        />
                      )}
                      <div>
                        <h3 className="text-xl font-semibold">{job.job_title}</h3>
                        <p className="text-muted-foreground">{job.company_name}</p>
                      </div>
                    </div>
                    
                    {job.location && (
                      <p className="text-sm text-muted-foreground mb-2">{job.location}</p>
                    )}

                    <div className="flex items-center gap-4 text-sm">
                      {job.match_score && (
                        <span>
                          Match Score: 
                          <span className="ml-2 font-semibold text-green-500">
                            {job.match_score}%
                          </span>
                        </span>
                      )}
                      {job.ats_score && (
                        <span>
                          ATS Score: 
                          <span className="ml-2 font-semibold text-green-500">
                            {job.ats_score}/100
                          </span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {job.extension_task_id && job.status === 'ready' && (
                      <Badge variant="outline" className="gap-1 text-xs">
                        <Zap className="h-3 w-3" />
                        Extension Active
                      </Badge>
                    )}
                    {getStatusBadge(job.status, !!job.extension_task_id)}
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t">
                  {job.status === 'preparing' && (
                    <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-orange-500 animate-spin" />
                        <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                          AI is generating custom materials...
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Creating resume, cover letter, and form data tailored to this job
                      </p>
                    </div>
                  )}

                  {job.status === 'ready' && (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                          Materials ready! Click to auto-apply with Chrome extension
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => handleReviewApply(job)}
                      disabled={job.status !== 'ready'}
                      className="gap-2 flex-1"
                      size="lg"
                    >
                      {job.status === 'ready' ? (
                        <>
                          <Zap className="h-4 w-4" />
                          Auto-Apply Now
                        </>
                      ) : (
                        <>
                          <Clock className="h-4 w-4 animate-spin" />
                          Preparing Materials...
                        </>
                      )}
                    </Button>

                    {job.status === 'ready' && (
                      <Button 
                        variant="outline" 
                        className="gap-2"
                        onClick={() => handleReviewApply(job)}
                      >
                        <FileText className="h-4 w-4" />
                        Review
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(job.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {selectedJob && (
          <SmartApplyModal
            job={selectedJob}
            open={showModal}
            onOpenChange={setShowModal}
            onApplied={loadQueue}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
